import { ChangeState, FSM } from 'afsm';
import { AudioPlayerMode, ILogger, IPlayer, PlayerState, VideoPlayerMode, TIMER_TYPE } from '../../enums';
import innerEmitter from '../../inner-emitter/inner-emitter';
import INNER_EVENT from '../../inner-emitter/inner-event';
import { CHROME_MAJOR_VERSION, IS_ANY_SAFARI, IS_CHROMIUM_BASE, IS_FIREFOX, IS_HONOR, IS_HUAWEI, IS_IOS, IS_IOS_15_1, IS_SAFARI } from '../../utils/environment';
import Timer from '../../utils/timer';
import { KIBANA_EVENT, NAME, PAUSED_RETRY_COUNT } from '../constants';
import ErrorCode from '../error-code';
import { generateErrorMessage } from '../error-generator';
import { ErrorName } from '../error-info';
import { PlayerEvent } from '../event';
import RtcError from '../rtc-error';
import * as eventManager from '../../manager/event-manager';
import eventLogger from '../../utils/log/event-log';
import { PlayOptions, TrackType } from '../../track/track';
import { delay } from '../../utils/utils';
import { addPromiseRetry } from '../../utils/decorators/promise-retry';
import kvStatManager, { KV_REPORT_KEY_AUDIO_PLAYER, KV_REPORT_KEY_VIDEO_PLAYER } from '../../manager/kv-stat-manager';
import { isAutoPlayDialogShowing, VideoPlayer } from '../..';
// 用户播放视频后，不代表界面上显示了video标签，显示与否需要调用show和hide方法
export abstract class Player<T extends AudioOrVideo> extends FSM<{
  [PlayerEvent.MEDIA_TRACK_CHANGED]: TrackType<T> | null;
  [PlayerEvent.PLAYER_STATE_CHANGED]: {
    type: string;
    state: PlayerState;
    reason: string;
  };
  [PlayerEvent.LOAD_START]: void;
  [PlayerEvent.LOADED_DATA]: void;
  [PlayerEvent.LOADED_META_DATA]: void;
  [PlayerEvent.ERROR]: MediaError;
  [PlayerEvent.AUTOPLAY_FAILED]: RtcError;
  [PlayerEvent.RESIZE]: { newWidth: number, newHeight: number };
  [PlayerEvent.TIME_UPDATE]: number;
  [PlayerEvent.ENTER_PICTURE_IN_PICTURE]: void;
  [PlayerEvent.LEAVE_PICTURE_IN_PICTURE]: void;
  [PlayerEvent.USER_RESUME_IN_PIP_OR_FULL_SCREEN]: void;
  [PlayerEvent.USER_PAUSE_IN_PIP_OR_FULL_SCREEN]: void;
  [PlayerEvent.ENTER_FULL_SCREEN]: void;
  [PlayerEvent.LEAVE_FULL_SCREEN]: void;
  [PlayerEvent.VOLUME_CHANGE]: { muted: boolean | undefined };
}> implements IPlayer {
  static PlayerEvent = PlayerEvent;
  id: string;
  element: (T extends typeof NAME.AUDIO ? HTMLAudioElement : HTMLVideoElement) | null = null;
  track: PlayOptions<T>['track'];
  url: string | null;
  attr?: { id: string, style: any, muted: boolean; };
  mode: T extends typeof NAME.AUDIO ? AudioPlayerMode : VideoPlayerMode;
  muted: boolean;
  protected _log: ILogger;
  isPausedByUserCall = false;
  protected _pausedRetryCount: number;
  protected _isElementPlayingFired = false;
  protected _interval: number;
  private _delayDestroyTimeoutId = 0;
  private _playSuccessResolve: (value: unknown) => void; // 如果降级播放，不使用 video 元素，则手动 resolve play 的 promise 防止无限 pending
  declare _state: PlayerState;

  private _isReplayByRecreateMediaStreamCalled = false;
  isPlayCalled = false;

  isInAutoPlayFailedState = false;
  isBindAutoPlayEvent = false;
  constructor(public options: PlayOptions<T>, public kind: T) {
    super(options.id, `${kind}-player`);
    this.id = options.id;
    this._log = options.log;
    this.track = options.track;
    this.muted = options.muted;
    this._pausedRetryCount = PAUSED_RETRY_COUNT;
    this._state = PlayerState.STOPPED;
    this.bindTrackEvents();
    this._log.info(`create ${kind}-player ${this.id}`);
  }

  get isPlaying() {
    return this._state === PlayerState.PLAYING && this.element?.paused === false;
  }
  get isPaused() {
    return this._state === PlayerState.PAUSED || this.element?.paused === true;
  }
  get isStopped() {
    return this._state === PlayerState.STOPPED;
  }
  // TODO: v4 only
  setAttr(attr: { id: string, style: any, muted: boolean; }) {
    this.attr = attr;
  }

  setUrl(url: string | null) {
    if (this.track) {
      this.unbindTrackEvents();
      if (this.element) {
        this.element.srcObject = null;
      }
      this.track = null;
    }
    if (url !== this.url) {
      this.url = url;
      if (url !== null && this.element) {
        this.element.crossOrigin = 'anonymous';
        this.element.src = url;
      }
    }
  }

  abstract setTrack(track: MediaStreamVideoTrack | MediaStreamAudioTrack | null): void

  async play() {
    if (this.isPlaying) return;
    try {
      this.isPlayCalled = true;
      if (this._delayDestroyTimeoutId) {
        clearTimeout(this._delayDestroyTimeoutId);
        this._delayDestroyTimeoutId = 0;
        this.bindTrackEvents();
        this.bindElementEvents();
      }
      this.bindAutoPlayEvent();
      await new Promise((resolve, reject) => {
        this._playSuccessResolve = resolve;
        this.element!.play().then(resolve, reject);
      });
    } catch (error) {
      const errorMessage = generateErrorMessage({ key: ErrorName.PLAY_FAILED, data: { media: this.kind, error } });
      this._log.warn(error);
      if (errorMessage.includes('NotAllowedError')) {
        this.isInAutoPlayFailedState = true;
        throw new RtcError({ code: ErrorCode.PLAY_NOT_ALLOWED, message: errorMessage });
      }
    }
  }

  // 音频 jitterbuffer 会有缓存，当远端关闭麦克风后，需要 delay 一定时间再销毁 audio element，确保缓存的 jitterbuffer 数据被消费干净
  stop(jitterBufferDelay = 0) {
    this.isPlayCalled = false;
    this.isPausedByUserCall = false;
    this._isElementPlayingFired = false;
    this.unbindEvents();
    if (jitterBufferDelay > 0 && !IS_ANY_SAFARI) {
      if (!this._delayDestroyTimeoutId) {
        this.element?.remove();
        this._log.info(`destroy element after 3 * ${jitterBufferDelay}`);
        this._delayDestroyTimeoutId = setTimeout(() => this.destroyElement(), 3 * jitterBufferDelay);
      }
    } else {
      this.destroyElement();
    }
    this.handleStopped(NAME.ENDED);
    if (this._interval > 0) {
      Timer.clearTask(this._interval);
    }
  }

  destroyElement() {
    if (this.element) {
      this._log.debug('destroy element');
      this.element.remove();
      this.element.src = '';
      this.element.srcObject = null;
      this.element = null;
    }
    clearTimeout(this._delayDestroyTimeoutId);
    this._delayDestroyTimeoutId = 0;
  }

  pause() {
    this._log.info('pause');
    this.isPausedByUserCall = true;
    this.doPause();
  }

  doPause() {
    this.element?.pause();
  }

  resume(byUserCall = false) {
    this.isPausedByUserCall = false;
    return this.doResume(byUserCall);
  }

  doResume(byUserCall = false) {
    this._log.info('resume');
    if (this.isPausedByUserCall) return Promise.resolve();
    if (this.isPlaying) return Promise.resolve();
    // 经测试，在 iOS 15.1 中调用 resume 可能出现视频 playing 但是播放黑屏情况
    // 重新创建 video 播放则正常。此处调用 restartPlayback 接口重新创建 element 播放。
    if (IS_IOS_15_1) return this.replay();

    return this.play().catch(() => { }); // play 只抛出 自动播放失败，忽略。
  }

  setMuted(muted: boolean) {
    if (this.element) {
      this.element.muted = muted;
    }
    this.muted = muted;
  }

  replay() {
    this.stop();
    return this.play().catch(() => { }); // play 只抛出 自动播放失败，忽略。
  }

  bindElementEvents() {
    if (this.element) {
      const handler = this.handleElementEvent.bind(this);
      const em = eventManager.create(this.element, this.element)
        .add(NAME.PLAYING, handler)
        .add(NAME.ENDED, handler)
        .add(NAME.PAUSE, handler)
        .add(NAME.ERROR, handler)
        .add(NAME.LOADSTART, handler)
        .add(NAME.LOADEDDATA, handler)
        .add(NAME.LOADEDMETADATA, handler);
      return em;
    }
  }

  bindTrackEvents(track = this.track) {
    if (track) {
      const handler = this.handleTrackEvent.bind(this);
      eventManager?.create(track, track)
        .add(NAME.ENDED, handler)
        .add(NAME.MUTE, handler)
        .add(NAME.UNMUTE, handler);

      if (track.readyState === NAME.ENDED) {
        this.handleTrackEvent({
          type: NAME.ENDED
        });
      }
      if (track.muted) {
        this.handleTrackEvent({
          type: NAME.MUTE
        });
      }
    }
  }
  // 调用播放接口前，监听自动播放弹窗被点击事件，恢复播放。
  bindAutoPlayEvent() {
    if (this.isBindAutoPlayEvent) return;
    innerEmitter.on(INNER_EVENT.AUTOPLAY_DIALOG_CLICK_CONFIRM, this.resume, this);
    this.isBindAutoPlayEvent = true;
  }
  unbindTrackEvents(track = this.track) {
    if (track) {
      eventManager.remove(track);
    }
  }

  unbindEvents() {
    if (this.element) {
      eventManager.remove(this.element);
    }
    this.unbindTrackEvents();
    innerEmitter.off(INNER_EVENT.AUTOPLAY_DIALOG_CLICK_CONFIRM, this.resume, this);
    this.isBindAutoPlayEvent = false;
  }

  handleElementEvent(event: any) {
    const eventType = event.type;
    switch (eventType) {
      case NAME.PLAYING:
        if (!isAutoPlayDialogShowing()) {
          this.isInAutoPlayFailedState = false;
        }
        this._isElementPlayingFired = true;
        this._log.info(`${this.kind} player is playing`);
        this.handlePlaying(NAME.PLAYING);
        if (this._interval) {
          Timer.clearTask(this._interval);
          this._interval = -1;
        }
        break;
      case NAME.ENDED:
        this._log.info(`${this.kind} player is ended`);
        this.handleStopped(NAME.ENDED);
        break;
      case NAME.PAUSE:
        this._log.info(`${this.kind} player is paused`);
        this.handlePaused(NAME.PAUSE);
        break;
      case NAME.ERROR:
        if (this.element && this.element.error) {
          this.handlePaused(NAME.ERROR);
          const { code, message } = this.element.error;
          this._log.error(`${this.kind} ${this._log.isLocal ? 'local' : 'remote'} MediaError code: ${code} message: ${message} userAgent: ${navigator.userAgent}`);
          eventLogger.uploadEvent({
            log: `stat-${this.kind}-${KIBANA_EVENT.PLAYER_ERROR}-${code}-${navigator.userAgent}`,
            error: this.element.error
          });
          // 荣耀和华为设备会有 code3 播放失败问题，此时走 Webcodec 解码规避。
          if (IS_HONOR || IS_HUAWEI) {
            this.emit(PlayerEvent.ERROR, this.element.error);
          } else {
            this.replayByRecreateMediaStream(this.element.error);
          }
        }
        break;
      case NAME.LOADEDDATA:
        if (this.kind === NAME.VIDEO) {
          this.emit(PlayerEvent.LOADED_DATA);
        }
        break;
      case NAME.LOADEDMETADATA:
        if (this.kind === NAME.VIDEO) {
          this.emit(PlayerEvent.LOADED_META_DATA);
        }
        break;
      case NAME.LOADSTART:
        this.emit(PlayerEvent.LOAD_START);
        break;
    }
  }

  // Android Webview 部分设备在拔耳机时，下行音频会出现 code 3 错误，此时重新设置 srcObject 可以恢复播放。
  replayByRecreateMediaStream(error: MediaError) {
    if (this._isReplayByRecreateMediaStreamCalled) return;
    this._isReplayByRecreateMediaStreamCalled = true;
    return this.doReplayByRecreateMediaStream(1000)
      .then(() => {
        this._log.warn('replayByRecreateMediaStream success');
        eventLogger.uploadEvent({ log: 'stat-replayByRecreateMediaStream-success' });
        kvStatManager.addSuccessEvent({ key: this.kind === NAME.AUDIO ? KV_REPORT_KEY_AUDIO_PLAYER.MEDIA_ERROR_RETRY : KV_REPORT_KEY_VIDEO_PLAYER.MEDIA_ERROR_RETRY });
      })
      .catch(() => {
        this._log.error('replayByRecreateMediaStream failed');
        eventLogger.uploadEvent({ log: 'stat-replayByRecreateMediaStream-failed' });
        kvStatManager.addFailedEvent({ key: this.kind === NAME.AUDIO ? KV_REPORT_KEY_AUDIO_PLAYER.MEDIA_ERROR_RETRY : KV_REPORT_KEY_VIDEO_PLAYER.MEDIA_ERROR_RETRY, error: this.element?.error });
        // 恢复播放失败，抛出错误走 webcodec 解码。
        this.emit(PlayerEvent.ERROR, error);
      });
  }

  @addPromiseRetry({
    settings: { retries: 2, timeout: 0 },
    onError(error, retry, reject, retryFuncArgs) {
      retryFuncArgs[0] = (retryFuncArgs[0] || 1000) + 1000;
      retry();
    }
  })
  doReplayByRecreateMediaStream(time: number) {
    this._log.warn(`delay ${time}ms to recreate mediaStream`);
    return new Promise<void>((resolve, reject) => {
      delay(time).then(() => {
        if (this.element) {
          this.element.srcObject = null;
          this.element.srcObject = new MediaStream([this.track!]);
          this._log.warn('recreated mediaStream');

          this.element.onerror = () => {
            this._log.warn(`element onerror ${this.element?.error?.code} fired after recreated mediaStream`);
            reject(this.element?.error);
          };
        }
        // 5s 内没有再出现 onerror，则恢复成功，否则恢复失败
        delay(5000).then(() => {
          if (!this.isPlaying || this.element?.error) {
            reject(this.element?.error);
          }
          resolve();
        });
      });
    }).finally(() => {
      if (this.element) this.element.onerror = null;
    });
  }
  async handleTrackEvent(event: any) {
    const eventType = event.type;
    if (this.options.enableLogTrackState) {
      this._log[eventType === NAME.UNMUTE ? 'info' : 'warn'](`track ${eventType}`);
    }
    switch (eventType) {
      case NAME.ENDED:
        this.handleStopped(NAME.ENDED);
        break;
      case NAME.MUTE:
        this.handlePaused(NAME.MUTE);
        break;
      case NAME.UNMUTE:
        if (this.mode > 0) { // 降级播放
          this.handlePlaying(this.mode.toString());
        } else if (this.element) {
          if (this.element.paused && !this.isPausedByUserCall) {
            this._log.warn('track unmuted and element is paused, resume');
            await this.doResume();
          }
          // TODO: 后续用 rx 优化这里的状态管理
          // 1. 在 element playing 之后，触发 track unmute 事件，才变更 playing 状态。
          //    否则会出现 element 还未 playing 成功，remote track 触发了 unmute，业务侧会收到 playing 事件，但实际上还未播放成功。
          // 2. 远端 track mute unmute 的时候，element 状态不会变更，在 resume 之后要检查下 element 状态，如果非 paused，需要补 playing 事件。
          if (this.element && !this.element.paused && this._isElementPlayingFired) {
            this.handlePlaying(NAME.UNMUTE);
          }
        }
        break;
    }
  }

  @ChangeState([], PlayerState.PLAYING, {
    sync: true,
    success(this: Player<AudioOrVideo>, reason: string) {
      this.emit(PlayerEvent.PLAYER_STATE_CHANGED, {
        type: this.kind,
        state: PlayerState.PLAYING,
        reason
      });
    }
  })
  handlePlaying(reason: string) {
    this._log.debug('handlePlaying', reason);
    this._playSuccessResolve?.(reason);
    return reason;
  }

  @ChangeState(PlayerState.PLAYING, PlayerState.PAUSED, {
    ignoreError: true,
    sync: true,
    success(this: Player<AudioOrVideo>, reason: string) {
      this.emit(PlayerEvent.PLAYER_STATE_CHANGED, {
        type: this.kind,
        state: PlayerState.PAUSED,
        reason
      });
    }
  })
  handlePaused(reason: string) {
    this._log.debug('handlePaused', reason);
    return reason;
  }

  @ChangeState([], PlayerState.STOPPED, {
    sync: true,
    success(this: Player<AudioOrVideo>, reason: string) {
      this.emit(PlayerEvent.PLAYER_STATE_CHANGED, {
        type: this.kind,
        state: PlayerState.STOPPED,
        reason
      });
    }
  })
  handleStopped(reason: string) {
    this._log.debug('handleStopped', reason);
    return reason;
  }

  getElement() {
    return this.element;
  }
}
